import React, { useState } from 'react';
import {
  AlertTriangle, BarChart3, BookOpen, Calculator, CheckCircle2,
  ChevronDown, ClipboardCheck, Copy, Database, FileSearch, FlaskConical,
  MapPinned, Microscope, ShieldCheck,
} from 'lucide-react';
import type { PreviewAnswer, TableBlock } from '../../lib/api/geologist';
import BoreholeMap from './BoreholeMap';
import DataChart from './DataChart';
import GeoSection from './GeoSection';
import LocalCoordinatePlan from './LocalCoordinatePlan';
import MineralogyPanel from './MineralogyPanel';
import { GEO_COPY, type GeoUiLang } from './geoWorkspaceI18n';

const PAGE_SIZE = 12;

function EvidenceTable({ table, lang, testId }: { table: TableBlock; lang: GeoUiLang; testId: string }) {
  const copy = GEO_COPY[lang];
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? table.rows : table.rows.slice(0, PAGE_SIZE);
  const total = table.total_rows ?? table.rows.length;
  const canExpand = table.truncated || table.rows.length > PAGE_SIZE;

  return (
    <div className="geo-data-table" data-testid={testId}>
      <div className="geo-data-table__header">
        <div>
          <h4>{table.title}</h4>
          <span>{total} {copy.records}</span>
        </div>
        <Database size={17} aria-hidden="true" />
      </div>
      <div className="geo-data-table__scroll">
        <table>
          <thead><tr>{table.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, columnIndex) => (
                  <td key={columnIndex}>{cell == null ? '—' : String(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canExpand && (
        <button className="geo-text-button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? copy.showLess : `${copy.showAll} (${total})`}
          <ChevronDown size={15} className={expanded ? 'is-rotated' : ''} aria-hidden="true" />
        </button>
      )}
      {table.note && <p className="geo-table-note">{table.note}</p>}
    </div>
  );
}

function DetailSection({
  title, icon, count, children, defaultOpen = false, tone = 'neutral', testId,
}: {
  title: string;
  icon: React.ReactNode;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  tone?: 'neutral' | 'warning' | 'info';
  testId?: string;
}) {
  return (
    <details className={`geo-answer-section geo-answer-section--${tone}`} open={defaultOpen} data-testid={testId}>
      <summary>
        <span className="geo-answer-section__icon">{icon}</span>
        <span>{title}</span>
        {count != null && <span className="geo-answer-section__count">{count}</span>}
        <ChevronDown className="geo-answer-section__chevron" size={17} aria-hidden="true" />
      </summary>
      <div className="geo-answer-section__body">{children}</div>
    </details>
  );
}

function BoreholeDetail({ data, lang }: {
  data: NonNullable<PreviewAnswer['borehole_detail']>;
  lang: GeoUiLang;
}) {
  const labels = {
    ru: { borehole: 'Скважина', report: 'в отчёте', site: 'Участок', context: 'Контекст', original: 'Координаты из отчёта', parsed: 'Разобрано системой', source: 'Источник', page: 'стр.', table: 'табл.', review: 'Проверка', required: 'требует проверки' },
    en: { borehole: 'Borehole', report: 'in report', site: 'Site', context: 'Context', original: 'Coordinates from report', parsed: 'Parsed by the system', source: 'Source', page: 'p.', table: 'table', review: 'Review', required: 'requires review' },
    hy: { borehole: 'Հորատանցք', report: 'հաշվետվությունում', site: 'Տեղամաս', context: 'Համատեքստ', original: 'Կոորդինատները հաշվետվությունից', parsed: 'Վերլուծված է համակարգով', source: 'Աղբյուր', page: 'էջ', table: 'աղ.', review: 'Ստուգում', required: 'պահանջում է ստուգում' },
  }[lang];
  return (
    <div className="geo-borehole-detail">
      <div className="geo-borehole-detail__title">
        <MapPinned size={18} aria-hidden="true" /> {labels.borehole} {data.borehole_code}
        {data.borehole_code_raw && data.borehole_code_raw !== data.borehole_code && (
          <span>({labels.report}: {data.borehole_code_raw})</span>
        )}
      </div>
      <div className="geo-borehole-detail__grid">
        {data.location && <div><span>{labels.site}</span><strong>{data.location}</strong></div>}
        {data.exploration_context && <div><span>{labels.context}</span><strong>{data.exploration_context}</strong></div>}
        <div><span>{labels.original}</span><strong className="geo-mono">{data.original_x_text ?? data.parsed_x ?? '—'} / {data.original_y_text ?? data.parsed_y ?? '—'}</strong></div>
        <div><span>{labels.parsed}</span><strong className="geo-mono">{data.parsed_x ?? '—'} / {data.parsed_y ?? '—'}</strong></div>
      </div>
      {(data.source_page || data.source_table) && (
        <p>{labels.source}: {data.source_code} · {labels.page} {data.source_page || '—'} · {labels.table} {data.source_table || '—'}</p>
      )}
      <p>{labels.review}: {data.verification_status_ru || '—'}{data.review_required ? ` · ${labels.required}` : ''}</p>
      {data.warnings.map((warning, index) => <div className="geo-inline-warning" key={index}><AlertTriangle size={14} />{warning}</div>)}
    </div>
  );
}

export interface GeologistAnswerProps {
  answer: PreviewAnswer;
  lang: GeoUiLang;
  onOpenSource?: (sourceId?: string | null, label?: string) => void;
}

const GeologistAnswer: React.FC<GeologistAnswerProps> = ({ answer, lang, onOpenSource }) => {
  const copy = GEO_COPY[lang];
  const [copied, setCopied] = useState(false);
  const reportCount = answer.report_groups.length;
  const bookCount = answer.report_groups.reduce((sum, group) => sum + group.book_source_codes.length, 0);

  const copySummary = async () => {
    await navigator.clipboard.writeText(answer.answer_summary);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <article className="geo-answer" data-testid="geo-answer">
      <section className="geo-answer-lead">
        <div className="geo-answer-lead__eyebrow">
          <span><ShieldCheck size={16} aria-hidden="true" />{copy.evidenceAnswer}</span>
          <button onClick={copySummary} className="geo-icon-text-button" aria-label={copy.copy}>
            {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
            {copied ? copy.copied : copy.copy}
          </button>
        </div>
        <p className="geo-answer-lead__summary">{answer.answer_summary}</p>
        <div className="geo-answer-lead__meta">
          {reportCount > 0 && <span><BookOpen size={14} />{reportCount} {copy.reports} · {bookCount} {copy.books}</span>}
          <span><ClipboardCheck size={14} />{answer.active_source_ids.length} {copy.activeSources}</span>
        </div>
        {answer.source_reference.length > 0 && (
          <div className="geo-source-chips" aria-label={copy.sourceEvidence}>
            {answer.source_reference.map((source) => (
              <button key={`${source.source_id}-${source.label}`} onClick={() => onOpenSource?.(source.source_id, source.label)}>
                {source.label}{source.report_year ? ` · ${source.report_year}` : ''}
              </button>
            ))}
          </div>
        )}
      </section>

      {answer.claims.length > 0 && (
        <section className="geo-findings" data-testid="geo-findings">
          <div className="geo-block-heading"><FileSearch size={18} /><span>{copy.findings}</span></div>
          <div className="geo-findings__list">
            {answer.claims.map((claim, index) => (
              <div className="geo-finding" key={`${claim.text}-${index}`}>
                <span className="geo-finding__index">{String(index + 1).padStart(2, '0')}</span>
                <div><p>{claim.text}</p>
                  <div className="geo-finding__refs">{claim.evidence_ids.map((id) => <code key={id}>{id}</code>)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {answer.metric_cards.length > 0 && (
        <section data-testid="geo-metrics">
          <div className="geo-block-heading"><BarChart3 size={18} /><span>{copy.metrics}</span></div>
          <div className="geo-metric-grid">
            {answer.metric_cards.map((metric) => (
              <div className="geo-metric" key={`${metric.label}-${metric.value}`}>
                <span>{metric.label}</span>
                <strong>{metric.value}{metric.unit && <small> {metric.unit}</small>}</strong>
                {metric.hint && <p>{metric.hint}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {(answer.mineralogy || answer.geo_section || (answer.charts?.length ?? 0) > 0 || answer.local_spatial_plan || answer.map || answer.borehole_detail) && (
        <section className="geo-visual-stack" data-testid="geo-visual-analysis">
          <div className="geo-block-heading"><Microscope size={18} /><span>{copy.visualAnalysis}</span></div>
          {answer.mineralogy && <MineralogyPanel b={answer.mineralogy} lang={lang} />}
          {answer.geo_section && <GeoSection s={answer.geo_section} lang={lang} />}
          {answer.charts?.map((chart, index) => <DataChart key={`${chart.title}-${index}`} c={chart} lang={lang} />)}
          {answer.borehole_detail && <BoreholeDetail data={answer.borehole_detail} lang={lang} />}
          {answer.local_spatial_plan && <div className="geo-embedded-panel"><LocalCoordinatePlan plan={answer.local_spatial_plan} lang={lang} /></div>}
          {answer.map && <div className="geo-embedded-panel"><BoreholeMap block={answer.map} loading={false} lang={lang} /></div>}
        </section>
      )}

      {answer.conflict_alert && answer.conflict_alert.items.length > 0 && (
        <DetailSection title={answer.conflict_alert.title || copy.conflicts} icon={<AlertTriangle size={17} />} count={answer.conflict_alert.items.length} defaultOpen tone="warning" testId="geo-conflicts">
          <div className="geo-warning-list">
            {answer.conflict_alert.items.map((item, index) => (
              <div key={index}><strong>{item.object}</strong><span>{item.kind_ru}</span>
                {(item.printed || item.calculated) && <p>{item.printed || '—'} ↔ {item.calculated || '—'}</p>}
                {item.note && <p>{item.note}</p>}
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {(answer.evidence_table || answer.comparison_table) && (
        <DetailSection title={copy.evidenceTables} icon={<Database size={17} />} defaultOpen testId="geo-evidence-tables">
          {answer.evidence_table && <EvidenceTable table={answer.evidence_table} lang={lang} testId="geo-evidence-table" />}
          {answer.comparison_table && <EvidenceTable table={answer.comparison_table} lang={lang} testId="geo-comparison-table" />}
        </DetailSection>
      )}

      {answer.calculation_breakdown.length > 0 && (
        <DetailSection title={copy.calculations} icon={<Calculator size={17} />} count={answer.calculation_breakdown.length}>
          <div className="geo-calculation-list">
            {answer.calculation_breakdown.map((calculation, index) => (
              <div key={index}>
                <strong>{calculation.title}</strong>
                <code>{calculation.expression}</code>
                <span>{calculation.result ?? '—'} {calculation.unit || ''}</span>
                {calculation.note && <p>{calculation.note}</p>}
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {answer.missing_evidence_warning && answer.missing_evidence_warning.items.length > 0 && (
        <DetailSection title={answer.missing_evidence_warning.title || copy.missingEvidence} icon={<FlaskConical size={17} />} count={answer.missing_evidence_warning.items.length} tone="info">
          <ul className="geo-clean-list">{answer.missing_evidence_warning.items.map((item, index) => <li key={index}>{item}</li>)}</ul>
        </DetailSection>
      )}

      {answer.coverage_notes.length > 0 && (
        <DetailSection title={copy.coverage} icon={<ClipboardCheck size={17} />} count={answer.coverage_notes.length}>
          <ul className="geo-clean-list">{answer.coverage_notes.map((note, index) => <li key={index}>{note}</li>)}</ul>
        </DetailSection>
      )}

      {answer.expert_commentary && (
        <DetailSection title={copy.expertCommentary} icon={<FlaskConical size={17} />} tone="info">
          <p className="geo-prose">{answer.expert_commentary}</p>
        </DetailSection>
      )}

      {answer.global_map_state?.status === 'blocked' && (
        <DetailSection title={copy.globalMap} icon={<MapPinned size={17} />} tone="warning">
          <p className="geo-prose">{answer.global_map_state.user_text}</p>
        </DetailSection>
      )}

      {answer.debug && (
        <DetailSection title={copy.technicalDetails} icon={<Database size={17} />}>
          <div className="geo-debug-grid">
            <span>intent</span><code>{answer.debug.intent}</code>
            <span>facts</span><code>{answer.debug.raw_fact_count}</code>
            <span>conflicts</span><code>{answer.debug.conflict_count}</code>
            <span>coordinate rows</span><code>{answer.debug.coordinate_rows_detected}</code>
          </div>
        </DetailSection>
      )}
    </article>
  );
};

export default GeologistAnswer;
